import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { colors, typography } from '../../styles/theme';
import { appModalSecondaryButtonSx } from '../../Components/appModalChrome.styles';
import {
  formatCollectionInquiryRow,
  getCollectionInquiryMailto,
  getCollectionInquiryOpenTarget,
  getCollectionInquiryTelHref,
} from '../../Map/utils/collectionInquiryDisplay';
import InquiryOriginTags from './InquiryOriginTags';

const contactLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0,
  maxWidth: '100%',
  fontSize: '12px',
  lineHeight: '16px',
  color: colors.textSecondary,
  fontFamily: typography.fontFamily.primary,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
};

function InquiryContactLink({ href, icon, label, ariaLabel }) {
  if (!href || !label) return null;
  return (
    <Box
      component="a"
      href={href}
      aria-label={ariaLabel}
      onClick={(event) => event.stopPropagation()}
      sx={contactLinkSx}
    >
      {icon}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
    </Box>
  );
}

function messagePreview(message) {
  const text = String(message || '').trim().replace(/\s+/g, ' ');
  if (!text) return 'Consulta';
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

/**
 * Tarjeta de una consulta (bandeja / “Leads y consultas”).
 * Sin chip Lead ni etapa: el lead vive en el grupo padre.
 *
 * `titleMode`:
 * - `visitor` (lista plana sin leads): nombre del visitante.
 * - `item` (anidada bajo un lead): label del lote / UF (si no `hideItemLabel`).
 *
 * `hideLeadName`: no muestra el nombre del visitante/lead (ya está en el header
 * del grupo). `hideItemLabel`: no muestra el lote/UF (ficha de un registro:
 * todas las consultas son de ese ítem). Con ambos, el título cerrado es el
 * mensaje (preview); expandida: contacto, mensaje, “Enviado el…”.
 *
 * @param {{
 *   row: object,
 *   expanded?: boolean,
 *   onToggle?: () => void,
 *   onOpenItem?: ((target: object) => void)|null,
 *   titleMode?: 'visitor'|'item',
 *   compact?: boolean,
 *   hideLeadName?: boolean,
 *   hideItemLabel?: boolean,
 * }} props
 */
export default function InquiryCard({
  row,
  expanded = false,
  onToggle,
  onOpenItem = null,
  titleMode = 'visitor',
  compact = false,
  hideLeadName = false,
  hideItemLabel = false,
}) {
  const display = formatCollectionInquiryRow(row);
  const openTarget = getCollectionInquiryOpenTarget(row);
  const canOpen = Boolean(openTarget && onOpenItem);
  const showVisitor = !hideLeadName && display.visitorName && display.visitorName !== 'Sin nombre';
  const showItem = !hideItemLabel && Boolean(display.itemLabel);
  const messageAsTitle = hideLeadName && hideItemLabel;

  let title = '';
  let subtitle = '';
  if (titleMode === 'item') {
    if (showItem) title = display.itemLabel;
    else if (messageAsTitle) title = messagePreview(display.message);
    else if (showVisitor) title = display.visitorName;
    else title = messagePreview(display.message);
    if (showVisitor && title !== display.visitorName) subtitle = display.visitorName;
  } else {
    if (showVisitor) title = display.visitorName;
    else if (showItem) title = display.itemLabel;
    else title = messagePreview(display.message);
    if (showItem && title !== display.itemLabel) subtitle = display.itemLabel;
  }

  return (
    <Box
      data-testid="inquiry-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        px: compact ? 1 : 1.25,
        py: compact ? 1 : 1.25,
        borderRadius: '8px',
        bgcolor: compact ? 'rgba(255,255,255,0.04)' : colors.backgroundDark,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle?.();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1.25,
          minHeight: compact ? 40 : 64,
          width: '100%',
          textAlign: 'left',
          cursor: onToggle ? 'pointer' : 'default',
        }}
      >
        {!compact ? (
          <Box
            aria-hidden
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.06)',
              color: display.unseen ? colors.primary : colors.textPrimary,
            }}
          >
            <MailOutlineIcon sx={{ fontSize: 16 }} />
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, minWidth: 0 }}>
            <Typography
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                fontSize: compact ? '12px' : '13px',
                lineHeight: '18px',
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.primary,
                fontWeight: display.unseen ? 600 : (compact ? 500 : 500),
                textAlign: 'left',
              }}
              noWrap
            >
              {title}
            </Typography>
            <InquiryOriginTags tags={display.originTags} />
            {display.unseen ? (
              <Box
                aria-label="Consulta nueva"
                sx={{
                  width: compact ? 7 : 8,
                  height: compact ? 7 : 8,
                  borderRadius: '50%',
                  bgcolor: colors.primary,
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Typography
              sx={{
                fontSize: '11px',
                color: colors.textSecondary,
                fontFamily: typography.fontFamily.primary,
                flexShrink: 0,
              }}
            >
              {display.timeShort}
            </Typography>
          </Box>
          {subtitle && !expanded ? (
            <Typography
              sx={{
                fontSize: '12px',
                lineHeight: '16px',
                color: colors.textSecondary,
                fontFamily: typography.fontFamily.primary,
              }}
              noWrap
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            pt: 1,
            pl: compact ? 0 : 5.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.75,
            textAlign: 'left',
            width: '100%',
          }}
        >
          {(display.visitorEmail || display.visitorPhone) ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <InquiryContactLink
                href={getCollectionInquiryMailto(display.visitorEmail)}
                label={display.visitorEmail}
                ariaLabel={`Enviar mail a ${display.visitorEmail}`}
                icon={<MailOutlineIcon sx={{ fontSize: 14, color: colors.textSecondary, flexShrink: 0 }} />}
              />
              <InquiryContactLink
                href={getCollectionInquiryTelHref(display.visitorPhone)}
                label={display.visitorPhone}
                ariaLabel={`Llamar a ${display.visitorPhone}`}
                icon={<PhoneOutlinedIcon sx={{ fontSize: 14, color: colors.textSecondary, flexShrink: 0 }} />}
              />
            </Box>
          ) : null}
          {display.message ? (
            <Typography
              sx={{
                fontSize: '13px',
                color: colors.textPrimary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {display.message}
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: '11px', color: colors.textSecondary }}>
            {[display.sentLabel, display.mailSent || display.manual ? null : 'El mail no salió']
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          {canOpen ? (
            <Button
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                onOpenItem(openTarget);
              }}
              sx={appModalSecondaryButtonSx}
            >
              Ver lote
            </Button>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
